import React, { useEffect, useRef, useState } from "react";
import {
  requestScreenWakeLock,
  releaseScreenWakeLock,
  vibrateForIncomingCall,
  playRingtone,
  stopRingtone,
  stopVibration,
} from "./screenWakeLock";
import {
  subscribeToDeliveryVideoCallNotifications,
  subscribeToPharmacistDriverVideoCallSession,
} from "./videoCallHelper";
import {
  acceptPharmacistDriverIncomingVideoCall,
  declinePharmacistDriverIncomingVideoCall,
  endPharmacistDriverIncomingVideoCall,
  endPharmacistDriverIncomingVideoCallOnUnload,
  fetchPharmacistDriverIncomingVideoCall,
} from "./deliveryVideoCallApi";
import { useAuthSelector } from "../../services/selectors/authSelector";
import AnimatedBottomSheet from "../common/animatedBottomSheet";
import Cookies from "js-cookie";
import { apiKey as ablyApiKey } from "../../networking/urlEndPoints";
import VideoCall from "./VideoCall";
import VideoCallErrorBoundary from "./VideoCallErrorBoundary";

const isFlutterNativeShell = () => {
  if (typeof window === "undefined") return false;
  const query = window.location?.search || "";
  try {
    return Boolean(
      window.__FLUTTER_NATIVE_VIDEO_CALLS__ ||
      window.DriverApp ||
      query.includes("flutter_app=1") ||
      window.sessionStorage?.getItem("flutter_app") === "1"
    );
  } catch (error) {
    return Boolean(
      window.__FLUTTER_NATIVE_VIDEO_CALLS__ ||
      window.DriverApp ||
      query.includes("flutter_app=1")
    );
  }
};

const postToFlutter = (payload) => {
  if (
    typeof window === "undefined" ||
    !window.DriverApp ||
    typeof window.DriverApp.postMessage !== "function"
  ) {
    return false;
  }
  try {
    window.DriverApp.postMessage(JSON.stringify(payload));
    return true;
  } catch (error) {
    return false;
  }
};

const normalizeCallData = (data) => {
  if (!data) return null;
  return {
    callId: data.call_id ?? data.callId,
    pharmacyId: data.pharmacy_id ?? data.pharmacyId,
    driverId:
      data.driver?.id != null
        ? String(data.driver.id)
        : data.driver_id != null
          ? String(data.driver_id)
          : data.driverId != null
            ? String(data.driverId)
            : undefined,
    driverName: data.driver?.name ?? data.driver_name ?? data.driverName,
    pharmacistId: data.pharmacist?.id ?? data.pharmacist_id ?? data.pharmacistId,
    pharmacistName: data.pharmacist?.name ?? data.pharmacist_name ?? data.pharmacistName,
    status: data.status,
    vonage_session_id: data.vonage_session_id,
    vonage_token: data.vonage_token,
    application_id: data.application_id,
    // Provider pinned by the backend; MediTalk calls also carry livekit_url.
    video_provider: data.video_provider,
    livekit_url: data.livekit_url,
    requested_at: data.requested_at,
    ring_expires_at: data.ring_expires_at,
    flow: data.flow,
  };
};

const getEventCallId = (payload) => payload?.call_id ?? payload?.callId;
const getEventStatus = (payload) => payload?.terminal_status ?? payload?.call_status ?? payload?.status;

const PharmacistDriverIncomingCallHandler = ({
  getPendingNotificationClick,
}) => {
  const { pharmacyId, driverID, isPharmacistForSelectedPharmacy, authLoginResponse } = useAuthSelector();
  const [incomingCall, setIncomingCall] = useState(null);
  const [showIncomingCallUI, setShowIncomingCallUI] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [callState, setCallState] = useState("idle");
  const incomingCallRef = useRef(null);
  const activeCallRef = useRef(null);
  const callStateRef = useRef("idle");
  const sessionEndSubscriptionRef = useRef(null);
  const unloadCleanupSentRef = useRef(false);

  const driverName =
    authLoginResponse?.data?.data?.driver_profile?.name ||
    authLoginResponse?.data?.data?.email?.split("@")?.[0] ||
    "Assistant";
  const shouldSuppressReactIncomingCalls = isFlutterNativeShell();

  useEffect(() => {
    activeCallRef.current = activeCall;
    unloadCleanupSentRef.current = false;
  }, [activeCall]);

  useEffect(() => {
    callStateRef.current = callState;
  }, [callState]);

  useEffect(() => {
    if (!pharmacyId || !driverID || isPharmacistForSelectedPharmacy) return undefined;

    const closeIncoming = () => {
      incomingCallRef.current = null;
      setShowIncomingCallUI(false);
      setIncomingCall(null);
      releaseScreenWakeLock();
      stopRingtone();
      stopVibration();
    };

    const openIncoming = async (rawData) => {
      const callId = rawData?.callId ?? rawData?.call_id;
      if (!callId || activeCallRef.current?.callId) return;
      const shouldSuppressAtArrival = isFlutterNativeShell();

      try {
        const details = await fetchPharmacistDriverIncomingVideoCall(callId);
        const normalized = normalizeCallData(details);
        if (!normalized?.callId) return;
        if (normalized.driverId != null && String(normalized.driverId) !== String(driverID)) return;
        if (normalized.status !== "requesting") {
          if (incomingCallRef.current?.callId === normalized.callId) closeIncoming();
          return;
        }

        if (shouldSuppressAtArrival || isFlutterNativeShell()) {
          postToFlutter({
            type: "pharmacistDriverIncomingCall",
            authToken: Cookies.get("authToken") || null,
            ablyApiKey: ablyApiKey || null,
            pharmacyId: normalized.pharmacyId || pharmacyId || null,
            driverID: normalized.driverId || driverID || null,
            driverName,
            pharmacistId: normalized.pharmacistId || null,
            pharmacistName: normalized.pharmacistName || "Pharmacist",
            callData: {
              ...normalized,
              flow: "pharmacist_to_driver",
              type: "pharmacist_driver_video_call_request",
            },
          });
          closeIncoming();
          return;
        }

        incomingCallRef.current = normalized;
        setIncomingCall(normalized);
        setShowIncomingCallUI(true);
        requestScreenWakeLock();
        vibrateForIncomingCall();
        playRingtone();

        if (
          typeof document !== "undefined" &&
          document.hidden &&
          navigator.serviceWorker?.controller
        ) {
          navigator.serviceWorker.controller.postMessage({
            type: "SHOW_NOTIFICATION",
            payload: {
              title: "Incoming Video Call",
              body: `${normalized.pharmacistName || "Pharmacist"} is calling`,
              data: {
                callId: normalized.callId,
                driverId: normalized.driverId,
                pharmacistId: normalized.pharmacistId,
                pharmacistName: normalized.pharmacistName,
                flow: "pharmacist_to_driver",
              },
            },
          });
        }
      } catch (error) {
        console.error("Error fetching pharmacist-driver video call:", error);
      }
    };

    const handleRealtimeEvent = (payload) => {
      if (!payload) return;

      if (payload.type === "pharmacist_driver_video_call_request") {
        if (payload.driver_id != null && String(payload.driver_id) !== String(driverID)) return;
        openIncoming(payload);
        return;
      }

      if (payload.type === "pharmacist_driver_video_call_status") {
        if (payload.driver_id != null && String(payload.driver_id) !== String(driverID)) return;
        const eventCallId = getEventCallId(payload);
        const eventStatus = getEventStatus(payload);
        if (
          incomingCallRef.current?.callId &&
          String(incomingCallRef.current.callId) === String(eventCallId) &&
          ["cancelled", "expired", "ended", "declined", "accepted"].includes(eventStatus)
        ) {
          closeIncoming();
        }
        if (
          activeCallRef.current?.callId &&
          String(activeCallRef.current.callId) === String(eventCallId) &&
          ["cancelled", "expired", "ended", "declined"].includes(eventStatus)
        ) {
          activeCallRef.current = null;
          setActiveCall(null);
          setCallState("idle");
          releaseScreenWakeLock();
          stopRingtone();
          stopVibration();
        }
      }
    };

    const ably = subscribeToDeliveryVideoCallNotifications({
      pharmacyId,
      onEvent: handleRealtimeEvent,
    });

    const handleFcmVideoCallRequest = (event) => {
      const data = event.detail?.data;
      if (data?.flow !== "pharmacist_to_driver" || !data?.callId) return;
      if (data.driverId != null && String(data.driverId) !== String(driverID)) return;
      openIncoming(data);
    };

    const applyPendingClick = (pending) => {
      const data = pending?.data;
      if (data?.flow !== "pharmacist_to_driver" || !data?.callId) return;
      openIncoming(data);
    };

    window.addEventListener("videoCallNotificationClick", handleFcmVideoCallRequest);
    window.addEventListener("videoCallRequestFromFCM", handleFcmVideoCallRequest);

    let clearTimeouts = () => {};
    if (typeof getPendingNotificationClick === "function") {
      applyPendingClick(getPendingNotificationClick());
      const t1 = setTimeout(() => applyPendingClick(getPendingNotificationClick()), 600);
      const t2 = setTimeout(() => applyPendingClick(getPendingNotificationClick()), 2000);
      clearTimeouts = () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }

    return () => {
      clearTimeouts();
      ably?.close?.();
      window.removeEventListener("videoCallNotificationClick", handleFcmVideoCallRequest);
      window.removeEventListener("videoCallRequestFromFCM", handleFcmVideoCallRequest);
    };
  }, [driverID, driverName, getPendingNotificationClick, isPharmacistForSelectedPharmacy, pharmacyId, shouldSuppressReactIncomingCalls]);

  useEffect(() => {
    if (!shouldSuppressReactIncomingCalls) return;
    incomingCallRef.current = null;
    activeCallRef.current = null;
    setIncomingCall(null);
    setShowIncomingCallUI(false);
    setActiveCall(null);
    setCallState("idle");
    releaseScreenWakeLock();
    stopRingtone();
    stopVibration();
  }, [shouldSuppressReactIncomingCalls]);

  useEffect(() => {
    if (!incomingCall) return undefined;
    requestScreenWakeLock();
    return () => {
      releaseScreenWakeLock();
    };
  }, [incomingCall]);

  useEffect(() => {
    if (!activeCall?.callId) {
      sessionEndSubscriptionRef.current?.close?.();
      sessionEndSubscriptionRef.current = null;
      return undefined;
    }

    const subscription = subscribeToPharmacistDriverVideoCallSession(
      activeCall.callId,
      () => {
        activeCallRef.current = null;
        setActiveCall(null);
        setCallState("idle");
        releaseScreenWakeLock();
        stopRingtone();
        stopVibration();
      },
      { pharmacyId },
    );

    sessionEndSubscriptionRef.current = subscription;

    return () => {
      subscription?.close?.();
      if (sessionEndSubscriptionRef.current === subscription) {
        sessionEndSubscriptionRef.current = null;
      }
    };
  }, [activeCall?.callId, pharmacyId]);

  useEffect(() => {
    const endActiveCallBeforePageLeaves = () => {
      const current = activeCallRef.current;
      if (!current?.callId || unloadCleanupSentRef.current) return;
      if (callStateRef.current !== "accepted") return;

      unloadCleanupSentRef.current = true;
      endPharmacistDriverIncomingVideoCallOnUnload(current.callId);
    };

    window.addEventListener("pagehide", endActiveCallBeforePageLeaves);
    window.addEventListener("beforeunload", endActiveCallBeforePageLeaves);

    return () => {
      window.removeEventListener("pagehide", endActiveCallBeforePageLeaves);
      window.removeEventListener("beforeunload", endActiveCallBeforePageLeaves);
    };
  }, []);

  const handleAccept = async () => {
    if (!incomingCall?.callId) return;
    stopRingtone();
    stopVibration();

    try {
      const accepted = normalizeCallData(await acceptPharmacistDriverIncomingVideoCall(incomingCall.callId, "delivery-app"));
      const nextCall = { ...incomingCall, ...accepted };
      incomingCallRef.current = null;
      setIncomingCall(null);
      setShowIncomingCallUI(false);
      setActiveCall(nextCall);
      setCallState("accepted");
      releaseScreenWakeLock();
    } catch (error) {
      console.error("Error accepting pharmacist-driver video call:", error);
    }
  };

  const handleDecline = async () => {
    if (!incomingCall?.callId) return;
    const callToDecline = incomingCall;
    stopRingtone();
    stopVibration();
    setShowIncomingCallUI(false);
    incomingCallRef.current = null;
    setIncomingCall(null);
    releaseScreenWakeLock();
    try {
      await declinePharmacistDriverIncomingVideoCall(callToDecline.callId);
    } catch (error) {
      console.error("Error declining pharmacist-driver video call:", error);
    }
  };

  const closeSheet = () => {
    setShowIncomingCallUI(false);
    stopRingtone();
    stopVibration();
  };

  const handleCloseActiveCall = () => {
    const callId = activeCallRef.current?.callId;
    if (callId) {
      endPharmacistDriverIncomingVideoCall(callId).catch(() => {});
    }
    setActiveCall(null);
    setCallState("idle");
  };

  const openSheet = () => setShowIncomingCallUI(true);

  const hasIncomingCallMinimized = incomingCall && !showIncomingCallUI;

  if (shouldSuppressReactIncomingCalls) return null;

  return (
    <>
      {hasIncomingCallMinimized && (
        <button
          type="button"
          onClick={openSheet}
          className="fixed bottom-4 left-4 right-4 z-[190] flex items-center gap-2 rounded-xl bg-slate-950 px-4 py-3 text-white shadow-lg"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-800">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </span>
          <span className="min-w-0 flex-1 text-left">
            <span className="block truncate text-sm font-semibold">Incoming Video Call</span>
            <span className="block truncate text-xs text-blue-100">{incomingCall?.pharmacistName || "Pharmacist"} is calling</span>
          </span>
          <span className="text-sm font-medium text-blue-200">Tap to open</span>
        </button>
      )}

      <AnimatedBottomSheet
        isOpen={!!(showIncomingCallUI && incomingCall)}
        onClose={closeSheet}
        ariaLabel="Incoming Video Call"
      >
            <div className="relative overflow-hidden rounded-t-[28px] bg-white px-6 pb-8 pt-2">
              <div className="mb-5 flex items-center justify-center pt-1">
                <div className="h-1 w-12 rounded-full bg-gray-300" />
              </div>

              <div className="mb-8 flex items-center gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-800 text-white shadow-md">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-lg font-bold text-gray-900">Incoming Video Call</p>
                  <p className="truncate text-sm text-gray-600">{incomingCall?.pharmacistName || "Pharmacist"} is calling</p>
                </div>
              </div>

              <div className="flex gap-3">
                <button type="button" onClick={handleAccept} className="flex-1 rounded-xl bg-emerald-500 px-4 py-3 text-sm font-semibold text-white shadow-md">
                  Accept
                </button>
                <button type="button" onClick={handleDecline} className="flex-1 rounded-xl border border-gray-200 bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-800">
                  Decline
                </button>
              </div>
            </div>
      </AnimatedBottomSheet>

      {activeCall && (
        <VideoCallErrorBoundary onClose={handleCloseActiveCall}>
          <VideoCall
            isOpen={!!activeCall}
            onClose={handleCloseActiveCall}
            patientData={{ first_name: activeCall.pharmacistName || "Pharmacist", last_name: "" }}
            driverName={driverName}
            pharmacistName={activeCall.pharmacistName || "Pharmacist"}
            callState={callState}
            isCaller={true}
            vonageSessionId={activeCall.vonage_session_id}
            vonageToken={activeCall.vonage_token}
            applicationId={activeCall.application_id}
            videoProvider={activeCall.video_provider}
            livekitUrl={activeCall.livekit_url}
          />
        </VideoCallErrorBoundary>
      )}
    </>
  );
};

export default PharmacistDriverIncomingCallHandler;
